import { useRef } from 'react'
import type { Editor } from '@tiptap/core'
import { ReactRenderer, useEditor as useTiptapEditor } from '@tiptap/react'
import { useRouter } from 'next/router'
import type { Suggestion } from 'shared/components/suggest-mention/types'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { getSuggestionsListKey } from 'modules/community/api/use-suggestions-list'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import {
  MentionSuggestionList,
  type MentionSuggestionListRef,
} from 'modules/community/components/editor/extensions/mention-suggestion-list'
import { createPostExtensions } from 'modules/community/components/editor/extensions/post-extensions'
import { docToHtml, htmlToDoc } from 'modules/community/utils/post-html'

interface UseEditorProps {
  contentHtml?: string
  mentions?: MentionInterface[]
  onHtmlChange?: (html: string) => void
}

export const useEditor = ({
  contentHtml,
  mentions = [],
  onHtmlChange,
}: UseEditorProps): {
  editor: Editor | null
  insertEmoji: (emoji: string) => void
  clearContent: () => void
} => {
  const { query } = useRouter()
  const communityPathRef = useRef(query.communityPath)
  communityPathRef.current = query.communityPath

  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.get, Suggestion[]>({
    method: RequestMethodsEnum.get,
  })
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const onHtmlChangeRef = useRef(onHtmlChange)
  onHtmlChangeRef.current = onHtmlChange

  const editor = useTiptapEditor({
    extensions: createPostExtensions({
      mentionSuggestion: {
        char: '@',
        debounce: 300,
        dismissOnOutsideClick: false,
        items: async ({ query: search }): Promise<Suggestion[]> => {
          const path = communityPathRef.current
          if (!path) return []
          try {
            return (
              (await fetcherRef.current(
                getSuggestionsListKey(path, undefined, search || undefined),
              )) ?? []
            )
          } catch {
            return []
          }
        },
        render: () => {
          let component: ReactRenderer<MentionSuggestionListRef> | null = null
          let unmount: (() => void) | undefined

          return {
            onStart: props => {
              component = new ReactRenderer(MentionSuggestionList, {
                props,
                editor: props.editor,
              })
              component.element.style.zIndex = '9999'
              unmount = props.mount(component.element)
            },
            onUpdate: props => {
              component?.updateProps(props)
            },
            onKeyDown: props => {
              if (props.event.key === 'Escape') return false
              return component?.ref?.onKeyDown(props) ?? false
            },
            onExit: () => {
              unmount?.()
              component?.destroy()
              component = null
              unmount = undefined
            },
          }
        },
      },
    }),
    content: htmlToDoc(contentHtml ?? '', mentions),
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class:
          'max-h-[300px] min-h-[200px] overflow-x-hidden overflow-y-auto rounded-[5px] border border-text-secondary/30 bg-transparent p-[15px] outline-none focus:border-brand-primary [&_a]:text-brand-primary [&_a]:underline',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onHtmlChangeRef.current?.(docToHtml(ed.getJSON()))
    },
  })

  return {
    editor,
    insertEmoji: (emoji: string) => {
      editor?.chain().focus().insertContent(emoji).run()
    },
    clearContent: () => {
      editor?.commands.clearContent(true)
      onHtmlChangeRef.current?.('<br>')
    },
  }
}
